import { ApiResponse, GetParams, Pagination } from '@uservoice/rest-utils';
import axios from 'axios';

import { Team } from '@/core/api/collections/teams';

export type LicenseType = 'fullaccess_admin' | 'contributor' | 'helpdesk_admin';
export type UserState = 'active' | 'deleted' | 'invited' | 'utility';
export type ProductType = 'discovery' | 'validation';

export type CorePermissionToggle = {
  features: 'rw' | 'ro' | 'no';
  feedback: 'rw' | 'r+' | 'no';
  tickets: 'rw' | 'ro' | 'no';
  knowledgebase: 'rw' | 'ro' | 'no';
  settings: 'billing' | 'rw' | 'none';
}
export type CorePermissions = CorePermissionToggle & {
  licenseType: LicenseType;
  state: UserState;
  offDuty: boolean;
};
export type UvkPermission = {
  state: UserState;
  admin: boolean;
};
export type Permissions = {
  core?: CorePermissions;
  uvk?: UvkPermission;
}

export type User = {
  id: number;
  displayName?: string;
  email: string;
  avatarUrl?: string;
  lastLogin?: string;
  permissions: Permissions;
  teams: Team[];
};

export type Users = ApiResponse<'users', User>;
export type GetUsersParams = GetParams & {
  query?: string;
  emails?: string;
}

export const getUsers = async (params: GetUsersParams): Promise<[User[], Pagination]> => {
  const { data } = await axios.get<Users>('/api/account/users', { params });
  return [data.users, data.pagination];
};

export type UserParams = GetParams & {
  userId: string;
};
export const getUser = async (params: UserParams): Promise<[User, null]> => {
  const { userId, ...rest } = params;
  const { data } = await axios.get<User>(`/api/account/users/${userId}`, { params: rest });
  return [data, null];
};

export const updateUserPermissions = async (id: number, core?: CorePermissions, uvk?: UvkPermission) => {
  if (!core && !uvk) {
    return;
  }

  return axios.put(`/api/account/users/${id}/permissions`, { core: ensureValidCorePermissions(core), uvk });
};

export const updateUserTeams = async (userId: number, addedTeams: number[], removedTeams: number[]) => {
  return axios.put('/api/account/teams/update_memberships', {
    user_id: userId,
    add_to_team_ids: addedTeams,
    remove_from_team_ids: removedTeams,
  });
};

export const setOffDuty = async (id: number): Promise<void> => {
  return await axios.put(`/api/account/users/${id}/off_duty`);
};

export const setOnDuty = async (id: number): Promise<void> => {
  return await axios.put(`/api/account/users/${id}/on_duty`);
};

export const grantAccess = async (user: User, to: ProductType): Promise<void> => {
  let payload;
  if (to === 'discovery') {
    payload = { core: ensureValidCorePermissions(user.permissions.core) };
  } else {
    payload = { uvk: {} };
  }

  return axios.put(`/api/account/users/${user.id}/permissions/grant`, payload);
};

export const revokeAccess = async (user: User, from: ProductType): Promise<void> => {
  let payload;
  if (from === 'discovery') {
    payload = { core: true };
  } else {
    payload = { uvk: true };
  }

  return axios.put(`/api/account/users/${user.id}/permissions/revoke`, payload);
};

export type InviteUserParams = {
  core?: CorePermissions & {
    enabled?: boolean;
  };
  uvk?: UvkPermission & {
    enabled?: boolean;
  }
  replyTo: string;
  emails: string[];
}

export const inviteUsers = async (params: InviteUserParams, teamIds: number[]): Promise<{ data: User[] }> => {
  ensureValidCorePermissions(params.core);
  return await axios.post('/api/account/users', {
    ...params,
    teamIds,
  });
};

export const resendInvitation = async (id: number): Promise<void> => {
  return await axios.put(`/api/account/users/${id}/resend_invitation`);
};

const ensureValidCorePermissions = (permissions?: CorePermissions) => {
  if (!permissions) return;

  if (permissions.licenseType === 'contributor') {
    permissions.features = 'no';
    permissions.feedback = 'r+';
    permissions.tickets = 'no';
    permissions.knowledgebase = 'no';
    permissions.settings = 'none';
  } else if (permissions.licenseType === 'helpdesk_admin') {
    permissions.features = 'no';
    permissions.feedback = 'r+';
  }

  return permissions;
};
